<div class="flex flex-col gap-4 p-5">
  <h1 class="headeradminui-text">LYFIS Scorecard Compiler</h1>

  <!-- 1. Upload Section -->
  <nz-card nzTitle="1. Upload Scorecard" class="w-full">
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap gap-4 items-center">
        <nz-select
          [(ngModel)]="selectedDiseaseId"
          nzPlaceHolder="Select Disease"
          [nzDisabled]="uploading || compiling"
          class="w-64"
        >
          <nz-option
            *ngFor="let d of diseases"
            [nzValue]="d.id"
            [nzLabel]="d.name"
          ></nz-option>
        </nz-select>

        <nz-upload
          [nzDisabled]="!selectedDiseaseId || uploading || compiling"
          [nzBeforeUpload]="beforeUpload"
          [nzShowUploadList]="false"
        >
          <button
            nz-button
            [disabled]="!selectedDiseaseId || uploading || compiling"
          >
            <span nz-icon nzType="upload"></span>
            Select Excel File
          </button>
        </nz-upload>

        <nz-spin *ngIf="uploading" [nzTip]="'Uploading...'"></nz-spin>
      </div>

      <div
        *ngIf="uploadedScorecard"
        class="flex flex-col gap-3 p-4 bg-gray-50 rounded"
      >
        <div class="flex items-center gap-2">
          <span class="font-medium">File:</span>
          <span>{{ uploadedScorecard.fileName }}</span>
        </div>
        <div class="flex flex-wrap gap-2 items-center">
          <nz-tag
            [nzColor]="getUploadStatusColor(uploadedScorecard.uploadStatus)"
          >
            Upload: {{ uploadedScorecard.uploadStatus }}
          </nz-tag>
          <nz-tag
            [nzColor]="getCompileStatusColor(uploadedScorecard.compileStatus)"
          >
            Compile: {{ uploadedScorecard.compileStatus }}
          </nz-tag>
        </div>
        <button
          nz-button
          nzType="primary"
          (click)="startCompilation()"
          [disabled]="uploadedScorecard.uploadStatus !== 'COMPLETED'"
        >
          <span nz-icon nzType="play-circle"></span>
          {{ compiling ? "Restarting..." : "Start Compilation" }}
        </button>
      </div>
    </div>
  </nz-card>

  <!-- 2. Chat / Compilation Section -->
  <nz-card
    *ngIf="uploadedScorecard && uploadedScorecard.uploadStatus === 'COMPLETED'"
    nzTitle="2. Compilation Process"
    class="w-full"
  >
    <div class="flex flex-col gap-4">
      <div class="bg-white w-full rounded" #chatScroll>
        <nz-list
          nzItemLayout="horizontal"
          [nzLoading]="compiling"
          class="demo-infinite-container max-h-96 overflow-y-auto"
        >
          @for (item of chatMessages; track item.id || $index) {
            <nz-list-item [attr.id]="item.id">
              <nz-list-item-meta
                nzAvatar="https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png"
                [nzDescription]="
                  item.sender === 'user' ? 'You' : 'Compiler Agent'
                "
              >
                <nz-list-item-meta-title>
                  <div
                    class="p-3 rounded-lg max-w-3xl"
                    [ngClass]="{
                      'bg-blue-500 text-white ml-auto': item.sender === 'user',
                      'bg-white border border-gray-200 mr-auto':
                        item.sender === 'agent',
                      'bg-red-50 border border-red-200 text-red-700': item.error
                    }"
                  >
                    <div class="whitespace-pre-wrap">{{ item.text }}</div>
                    <div class="text-xs opacity-70 mt-1 text-right">
                      {{ item.timestamp | date: "shortTime" }}
                    </div>
                  </div>
                </nz-list-item-meta-title>
              </nz-list-item-meta>
            </nz-list-item>
          }
          @if (chatMessages.length === 0) {
            <nz-list-empty />
          }
        </nz-list>
      </div>

      <nz-alert
        *ngIf="awaitingConfirmation"
        nzType="warning"
        nzMessage="Does the summary match your Excel sheet?"
        class="mb-4"
      >
        <div class="flex gap-2 mt-2">
          <button
            nz-button
            nzType="primary"
            nzSize="small"
            (click)="confirmSummary(true)"
          >
            Yes, Proceed
          </button>
          <button
            nz-button
            nzDanger
            nzSize="small"
            (click)="confirmSummary(false)"
          >
            No, Reject
          </button>
        </div>
      </nz-alert>

      <nz-alert
        *ngIf="awaitingWarningsConfirmation"
        nzType="warning"
        nzMessage="Compilation Warnings Detected"
        nzDescription="The compiler found some warnings. Review them in the chat above. Do you want to proceed with compilation?"
        class="mb-4"
      >
        <div class="flex gap-2 mt-2">
          <button
            nz-button
            nzType="primary"
            nzSize="small"
            (click)="confirmWarnings(true)"
          >
            Yes, Proceed with Warnings
          </button>
          <button
            nz-button
            nzDanger
            nzSize="small"
            (click)="confirmWarnings(false)"
          >
            No, Reject
          </button>
        </div>
      </nz-alert>

      <div class="flex gap-2">
        <textarea
          nz-input
          [(ngModel)]="chatInput"
          placeholder="Type your message..."
          (keydown.enter)="$event.preventDefault(); sendUserMessage()"
          [disabled]="compiling"
          class="flex-1"
          rows="3"
        ></textarea>
        <button
          nz-button
          nzType="primary"
          (click)="sendUserMessage()"
          [disabled]="!chatInput.trim() || compiling"
        >
          <span nz-icon nzType="send"></span>
          Send
        </button>
      </div>
    </div>
  </nz-card>

  <!-- 3. Results Section (Success) -->
  <nz-card
    *ngIf="uploadedScorecard?.compileStatus === 'ok'"
    nzTitle="3. Compiled Artifacts"
    class="w-full"
  >
    <div class="flex flex-col gap-4">
      <div>
        <h4 class="font-semibold mb-2">Config JSON</h4>
        <div
          class="relative bg-gray-900 text-gray-100 p-4 rounded overflow-auto max-h-96"
        >
          <button
            nz-button
            nzType="text"
            nzSize="small"
            class="absolute top-2 right-2 text-white hover:bg-gray-700"
            (click)="copyCompiledConfig()"
          >
            <span nz-icon nzType="copy"></span>
            Copy
          </button>
          <pre class="m-0 font-mono text-sm whitespace-pre-wrap">{{
            uploadedScorecard?.compiledConfig | json
          }}</pre>
        </div>
      </div>

      <div>
        <h4 class="font-semibold mb-2">Engine Code (JS)</h4>
        <div
          class="relative bg-gray-900 text-gray-100 p-4 rounded overflow-auto max-h-96"
        >
          <button
            nz-button
            nzType="text"
            nzSize="small"
            class="absolute top-2 right-2 text-white hover:bg-gray-700"
            (click)="copyCompiledEngineCode()"
          >
            <span nz-icon nzType="copy"></span>
            Copy
          </button>
          <pre class="m-0 font-mono text-sm whitespace-pre-wrap">{{
            uploadedScorecard?.compiledEngineCode
          }}</pre>
        </div>
      </div>
    </div>
  </nz-card>

  <!-- 3. Results Section (Failure) -->
  <nz-card
    *ngIf="compilationErrors && compilationErrors.length > 0"
    nzTitle="3. Compilation Failed"
    class="w-full"
  >
    <nz-alert
      nzType="error"
      nzMessage="Compilation was rejected. Please review the errors and fixes below:"
      class="mb-4"
    ></nz-alert>

    <nz-table
      [nzData]="formatErrors(compilationErrors)"
      [nzBordered]="true"
      [nzSize]="'small'"
      class="w-full"
    >
      <thead>
        <tr>
          <th nzWidth="56px">#</th>
          <th nzWidth="190px">Type</th>
          <th>Location</th>
          <th nzWidth="220px">Current</th>
          <th nzWidth="220px">Fix</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        @for (e of formatErrors(compilationErrors); track e.id) {
          <tr>
            <td class="font-semibold text-gray-600">{{ e.id }}</td>
            <td>
              <nz-tag
                [nzColor]="
                  e.type.includes('UNMAPPED') || e.type.includes('BOUNDARY')
                    ? 'orange'
                    : 'red'
                "
              >
                {{ e.type }}
              </nz-tag>
            </td>
            <td class="text-sm">{{ e.location }}</td>
            <td>
              <code class="bg-red-50 text-red-700 px-2 py-1 rounded text-xs">
                {{ e.current }}
              </code>
            </td>
            <td>
              <code
                class="bg-green-50 text-green-700 px-2 py-1 rounded text-xs"
              >
                {{ e.fix }}
              </code>
            </td>
            <td class="text-sm text-gray-600">{{ e.description }}</td>
          </tr>
        }
        @if (!compilationErrors || compilationErrors.length === 0) {
          <tr>
            <td colspan="6" class="py-4">
              <nz-list-empty />
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  </nz-card>
</div>
